import * as React from "react";
import { observer, inject } from "mobx-react";
import { Viewport, ViewSmall, ViewMedium } from "bernie-view";

import {
  UitkLayoutGrid,
  UitkLayoutGridItem,
  UitkLayoutConditionalGridColumnRow,
  UitkLayoutConditionalGridSpan,
} from "uitk-react-layout-grid";

import { Location } from "components/shared/Location/Location";
import { Dates } from "components/shared/Dates/Dates";
import { Travelers } from "components/shared/Travelers/Travelers";

import { WizardHotelPWAFilter } from "components/flexComponents/WizardHotelPWA/components/WizardHotelPWAFilter";
import { SubmitRow } from "components/shared/StorefrontWizard/SubmitRow/SubmitRow";
import { LobErrorSummary } from "components/shared/LobErrorSummary/LobErrorSummary";

import { HotelHiddenSearchData } from "../components/HotelHiddenSearchData";

import { NoRehydratationValues } from "components/shared/TravelersField/typings";
import { WizardHotelPWAv2Props } from "../typings";
import { Experiment, isVariantEnabled } from "src/components/utility/experiment";
import { WizardFlightAndCarOptions } from "../components/WizardFlightAndCarOptions";
import { CheckboxModuleNameTypes } from "src/components/shared/PropertyFIlterComponents/components/Checkbox";
import { locKeys } from "components/flexComponents/WizardHotelPWA/components/l10n";
import { StickyCTAMobile } from "components/flexComponents/WizardHotelPWA/components/StickyCTAMobile";
import { useLocalization } from "@shared-ui/localization-context";
import { RateCalendarFooter } from "components/flexComponents/WizardHotelPWA/components/RateCalendarFooter";

import { UitkSpacing } from "uitk-react-spacing";
import { UitkHeading } from "uitk-react-text";
import { UitkCardAloha } from "uitk-react-cards";
import { classNames } from "uitk-react-utils";
import { RateCalendarStore } from "src/stores/rateCalendar";

enum DateState {
  START = "startDate",
  END = "endDate",
}

function usePreviousValue(value: boolean) {
  const ref = React.useRef<boolean>(false);

  React.useEffect(() => {
    ref.current = value;
  }, []);

  return ref.current;
}

export const WizardHotelPWAv2HTGView: React.FC<WizardHotelPWAv2Props> = observer((props) => {
  const { context, wizardState, analytics, flexComponent, compositionStore } = props;
  const { formatText } = useLocalization();
  const isDesktop = !context?.deviceInformation?.mobile && !context?.deviceInformation?.tablet;

  const [wizardContainerIsOutOfWindow, setWizardContainerIsOutOfWindow] = React.useState(false);
  const [datePickerIsOpen, setDatePickerIsOpen] = React.useState(
    Boolean(isDesktop && wizardState.hotelWizardState.config.desktopAutoOpenCalendar)
  );

  const wizardRef = React.useRef<HTMLDivElement>(null);
  const wizardSmallViewContainer = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    window.addEventListener("scroll", onScroll);

    return () => {
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  const previousWizardContainerIsOutOfWindow = usePreviousValue(wizardContainerIsOutOfWindow);

  React.useEffect(() => {
    // this prevent the new date picker to stay opened when switching between sticky/non-sticky wizard
    if (previousWizardContainerIsOutOfWindow !== wizardContainerIsOutOfWindow) {
      setDatePickerIsOpen(false);
    }
  }, [wizardContainerIsOutOfWindow]);

  const onScroll = React.useCallback(() => {
    if (wizardRef && wizardRef.current) {
      const currentWizardRef = wizardRef.current.getBoundingClientRect().bottom;
      setWizardContainerIsOutOfWindow(currentWizardRef < 0);
    }
  }, [wizardRef, setWizardContainerIsOutOfWindow]);

  const getModuleNameForTracking = () => {
    // append sticky to the module name if needed
    // doesn't look at the device information since you can't interact with the wizard it if you don't see it.
    return wizardContainerIsOutOfWindow ? `${flexComponent.name}-sticky` : flexComponent.name;
  };

  const toggleDatePicker = () => {
    setDatePickerIsOpen(!datePickerIsOpen);
  };

  const heading = compositionStore?.composition?.pageHeading;
  const subHeading = compositionStore?.composition?.pageSubHeadline;
  const hasHeadings = heading || subHeading;
  const isMobile = context.deviceInformation?.mobile;
  const isSticky = wizardContainerIsOutOfWindow && !isMobile;
  const wizardFormProps = {
    flexComponent,
    wizardState,
    analytics,
    context,
    isSticky,
    toggleDatePicker,
    datePickerIsOpen,
    isDesktop,
  };

  return (
    <div className="WizardHotelPWAv2HTG WizardHotelPWA" ref={wizardRef}>
      <Viewport>
        <ViewSmall>
          <div ref={wizardSmallViewContainer}>
            {hasHeadings && (
              <UitkSpacing padding={{ inline: "three", blockstart: "six" }}>
                <div>
                  {heading && (
                    <UitkHeading level={1} size={700}>
                      {heading}
                    </UitkHeading>
                  )}
                  {subHeading && (
                    <UitkSpacing padding={{ blockstart: "two" }}>
                      <UitkHeading level={2} size={500}>
                        {subHeading}
                      </UitkHeading>
                    </UitkSpacing>
                  )}
                </div>
              </UitkSpacing>
            )}
            {isMobile && (
              <StickyCTAMobile
                isSticky={wizardContainerIsOutOfWindow}
                moduleName={getModuleNameForTracking()}
                submitButtonText={formatText(locKeys.stickyWizardCallToAction)}
                scrollTarget={wizardSmallViewContainer}
              />
            )}
            <WizardForm {...wizardFormProps} isMobile={true} />
          </div>
        </ViewSmall>
        <ViewMedium>
          <WizardForm {...wizardFormProps} isMobile={false} />
        </ViewMedium>
      </Viewport>
    </div>
  );
});

interface WizardFormProps
  extends Pick<WizardHotelPWAv2Props, "flexComponent" | "wizardState" | "analytics" | "context"> {
  isMobile: boolean;
  isSticky: boolean;
  toggleDatePicker: () => void;
  datePickerIsOpen: boolean;
  rateCalendar?: RateCalendarStore;
  isDesktop: boolean;
}

const WizardForm: React.FC<WizardFormProps> = inject("rateCalendar")(
  observer((props) => {
    const {
      context,
      wizardState,
      analytics,
      flexComponent,
      isMobile,
      isSticky,
      toggleDatePicker,
      datePickerIsOpen,
      rateCalendar,
      isDesktop,
    } = props;
    const {
      globalWizardState,
      hotelWizardState,
      hotelWizardState: { config, numberOfErrors, errorInputRef, moreThanOneError },
    } = wizardState;

    const { elementId } = config;

    const { formatText, formatDateString } = useLocalization();

    const mainGridColumns: UitkLayoutConditionalGridColumnRow = { small: 2, medium: 4, large: 12 };
    const datesColSpan: UitkLayoutConditionalGridSpan = { small: 2, medium: isSticky ? 6 : 2, large: isSticky ? 6 : 8 };
    const cardColSpan: UitkLayoutConditionalGridSpan = { small: 2, medium: 4, large: 12 };
    const locationColSpan: UitkLayoutConditionalGridSpan = { small: 2, large: 5 };
    const travelersColSpan: UitkLayoutConditionalGridSpan = { small: 2, medium: isSticky ? 4 : 2, large: 4 };
    const addFlightColSpan: UitkLayoutConditionalGridSpan = { small: 1, large: 2 };
    const addCarColSpan: UitkLayoutConditionalGridSpan = { small: 1, large: 2 };
    const popularFiltersColSpan: UitkLayoutConditionalGridSpan = { small: 1, large: 3 };
    const mainTitleWizardClassNames: UitkLayoutConditionalGridSpan = { small: 2, medium: 12, large: 12 };

    const [htgStartInvalidMessage, setStartInvalidMessage] = React.useState<string>("");
    const [htgEndInvalidMessage, setEndInvalidMessage] = React.useState<string>("");

    const isPopularFiltersEnabledVariant1 = isVariantEnabled(context, Experiment.LP_WIZARD_POPULAR_FILTERS_ON_HTG, 1);
    const isPopularFiltersEnabledVariant2 = isVariantEnabled(context, Experiment.LP_WIZARD_POPULAR_FILTERS_ON_HTG, 2);

    if (!hotelWizardState.regionId) {
      hotelWizardState.setRegionId(context.searchContext.location.id);
    }

    if (!isDesktop) {
      const dateFormat = hotelWizardState.config.date.format;
      hotelWizardState.setHtgStartDate(formatDateString(hotelWizardState.date.start, { raw: dateFormat }));
      hotelWizardState.setHtgEndDate(formatDateString(hotelWizardState.date.end, { raw: dateFormat }));
    }

    /**
     * Values used if no rehydratation(JS disabled)
     */
    const noRehydratationValues: NoRehydratationValues = {
      numOfTravelers: 2,
      name: "adults",
    };

    const setInputsRefs = (ref: React.RefObject<HTMLInputElement>, secondRef?: React.RefObject<HTMLInputElement>) => {
      hotelWizardState.wizardInputsArray.push(ref);
      if (secondRef) {
        hotelWizardState.wizardInputsArray.push(secondRef);
      }
    };

    const getLocError = (isLink: boolean) => {
      if (!isLink) {
        return hotelWizardState.moreThanOneError()
          ? config.form.submitButton.locMultipleWarningsToken
          : config.form.submitButton.locWarningToken;
      }

      return hotelWizardState.moreThanOneError()
        ? config.form.submitButton.locMultipleWarningsLinkToken
        : config.form.submitButton.locWarningLinkToken;
    };

    /**
     * When user add a flight to the current trip search,
     * a origin field is displayed so as a legend for multiple places (if enabled)
     * Fields below add a flight checkbox should be visible in mobile view
     * Following values are taken from UDS tokens:
     * http://uds.ch.lab.stockyard.io/expedia/tokens/#foundation-breakpoint
     */
    const onAddFlightCheckboxChange = () => {
      const VIEWPORT_MAX_SMALL_BREAKPOINT = 575;
      const SPACE_ABOVE_WIZARD_IN_PIXELS = 200;
      const SPACE_LEFT_WIZARD_IN_PIXELS = 0;
      const SCROLL_BEHAVIOR = "smooth";
      const { innerWidth: viewportSize } = window;

      if (viewportSize < VIEWPORT_MAX_SMALL_BREAKPOINT) {
        window.scrollTo({
          top: SPACE_ABOVE_WIZARD_IN_PIXELS,
          left: SPACE_LEFT_WIZARD_IN_PIXELS,
          behavior: SCROLL_BEHAVIOR,
        });
      }
    };

    const isValidDate = (date: Date) => {
      return date && date.toString() !== "Invalid Date";
    };

    const validate = (key: string) => {
      const checkInDate = new Date(hotelWizardState.startDate);
      const checkOutDate = new Date(hotelWizardState.endDate);

      if (key === DateState.START && !isValidDate(checkInDate)) {
        return formatText(locKeys.invalidFromDate);
      }

      if (key === DateState.END && !isValidDate(checkOutDate)) {
        return formatText(locKeys.invalidToDate);
      }

      if (key === DateState.END && isValidDate(checkInDate)) {
        const maxEndDate = new Date(checkInDate!.getFullYear(), checkInDate!.getMonth(), checkInDate!.getDate() + 28);

        if (checkOutDate < checkInDate) {
          return formatText(locKeys.invalidDateRangeEndBeforeStart);
        }

        if (checkOutDate > maxEndDate) {
          return formatText(locKeys.invalidDateRangeEndOverLimit);
        }
      }

      return "";
    };

    const validateAndGetFormErrors = (event?: React.FormEvent<HTMLFormElement>) => {
      const errors = [];
      const startDateError = validate(DateState.START);
      const endDateError = validate(DateState.END);

      setStartInvalidMessage(startDateError);
      setEndInvalidMessage(endDateError);

      if (startDateError) {
        errors.push(startDateError);
      }

      if (endDateError) {
        errors.push(endDateError);
      }

      if (errors.length > 0 && event && typeof event.preventDefault === "function") {
        event.preventDefault();
      }

      return errors;
    };

    const onSubmitForm = (e: React.FormEvent<HTMLFormElement>) => {
      if (validateAndGetFormErrors().length > 0) {
        e.preventDefault();
      }
    };

    const mainWizardContainerClassNames = classNames("wizardCard", {
      sticky: isSticky,
    });
    const wizardFormTestId = isSticky ? "wizard-sticky" : "wizard";
    const rateCalendarProps = rateCalendar?.isExperimentEnabled
      ? {
          datePickerContent: rateCalendar.datePickerContent,
          footerHtml: <RateCalendarFooter {...rateCalendar.footerHtmlProps} />,
          footerText: rateCalendar.footerText,
        }
      : {};

    return (
      <UitkSpacing
        padding={{
          small: isSticky ? undefined : { inline: "three" },
          medium: isSticky ? undefined : { inline: "six" },
        }}
      >
        <UitkLayoutGrid columns={mainGridColumns} className={mainWizardContainerClassNames}>
          <UitkLayoutGridItem colSpan={cardColSpan}>
            <UitkSpacing padding={isSticky ? { blockend: "three" } : { block: "six" }}>
              <UitkCardAloha>
                <form
                  noValidate={true}
                  id={elementId}
                  data-fm={flexComponent.fmId}
                  data-fm-title-id={flexComponent.fmTitleId}
                  onSubmit={onSubmitForm}
                  action={hotelWizardState.formAction}
                  data-testid={wizardFormTestId}
                >
                  <UitkLayoutGrid columns={mainGridColumns} space="three">
                    {numberOfErrors > 0 && (
                      <LobErrorSummary
                        locHeadingToken={getLocError(false)!}
                        locHeadingArgs={moreThanOneError() && [numberOfErrors]}
                        locLinkToken={getLocError(true)}
                        linkClickFocusId="roundtrip-error"
                        inputErrorRef={errorInputRef}
                        colSpan={mainGridColumns}
                        setInputsRefs={setInputsRefs}
                      />
                    )}
                    {isSticky && !isMobile && (
                      <UitkLayoutGridItem colSpan={mainTitleWizardClassNames}>
                        <h1 className={`uitk-type-500`}>{formatText(locKeys.stickyWizardTitle)}</h1>
                      </UitkLayoutGridItem>
                    )}
                    <Dates
                      lobState={hotelWizardState}
                      colSpan={datesColSpan}
                      setInputsRefs={setInputsRefs}
                      autoOpenCalendar={datePickerIsOpen}
                      toggleDatePicker={toggleDatePicker}
                      isDesktop={isDesktop}
                      {...rateCalendarProps}
                      htgStartInvalidMessage={htgStartInvalidMessage}
                      htgEndInvalidMessage={htgEndInvalidMessage}
                      validateAndGetFormErrors={validateAndGetFormErrors}
                    />

                    <Travelers
                      lobState={hotelWizardState}
                      colSpan={travelersColSpan}
                      noRehydratationValues={noRehydratationValues}
                    />

                    {isSticky && !isMobile && (
                      <SubmitRow
                        lobState={hotelWizardState}
                        rfrr="SearchClick_Hotel"
                        globalConfig={globalWizardState.config}
                        moduleName={CheckboxModuleNameTypes.WIZARD_HOTEL_V2_HTG}
                        hasStickyWizard={isSticky}
                        testId="wizard-submit-button"
                      />
                    )}
                  </UitkLayoutGrid>

                  {isPopularFiltersEnabledVariant1 && (
                    <UitkSpacing margin={{ blockstart: "four" }}>
                      <div className={isSticky ? "is-hidden" : ""}>
                        <UitkLayoutGrid columns={mainGridColumns}>
                          <WizardHotelPWAFilter
                            changeFormProperties={hotelWizardState.changeFormFilterProperties}
                            checkboxFilterChecks={hotelWizardState.checkboxFilterChecks}
                            advancedOptionsFilterVisible={hotelWizardState.advancedOptionsFilterVisible}
                            context={context}
                            popularFiltersColSpan={popularFiltersColSpan}
                            isDynamicFilters={true}
                          />
                        </UitkLayoutGrid>
                      </div>
                    </UitkSpacing>
                  )}

                  {isPopularFiltersEnabledVariant2 && (
                    <WizardFlightAndCarOptions
                      mainGridColumns={mainGridColumns}
                      addFlightColSpan={addFlightColSpan}
                      addCarColSpan={addCarColSpan}
                      hotelWizardState={hotelWizardState}
                      onAddFlightCheckboxChange={onAddFlightCheckboxChange}
                      analytics={analytics}
                      shouldNotDisplay={isSticky}
                    />
                  )}

                  {hotelWizardState.isAddAFlightChecked && (
                    <UitkLayoutGrid columns={mainGridColumns}>
                      <UitkLayoutGridItem colSpan={locationColSpan}>
                        <div>
                          <Location originField={true} destinationField={false} lobState={hotelWizardState} />
                        </div>
                      </UitkLayoutGridItem>
                    </UitkLayoutGrid>
                  )}

                  {!isSticky && (
                    <SubmitRow
                      lobState={hotelWizardState}
                      rfrr="SearchClick_Hotel"
                      globalConfig={globalWizardState.config}
                      moduleName={CheckboxModuleNameTypes.WIZARD_HOTEL_V2_HTG}
                      testId="wizard-submit-button"
                    />
                  )}

                  <HotelHiddenSearchData hotelWizardState={hotelWizardState} />
                </form>
              </UitkCardAloha>
            </UitkSpacing>
          </UitkLayoutGridItem>
        </UitkLayoutGrid>
      </UitkSpacing>
    );
  })
);

export default WizardHotelPWAv2HTGView;
